// One row per photo, on every job (the API's photo-table.ts; DATAVERSE.md
// "JSON fields or tables", DD 2026-10-22). The device makes the row as the
// photo is taken, offline too, with the photo's own id; it syncs like any
// record, ahead of the photo's upload, which fills in the file.
import { bearingToDirection, type Direction } from './compass.ts';
import type { PhotoPlace } from './condition-records';
import type { DeviceRecord, Fields } from './device-db';

export const PHOTO_TABLE = 'photo';
/** The area's own shot (#437): at most one per area, the latest uploaded. */
const FEATURE_PHOTO = 'Feature photo';
/** A condition report's photos are evidence of the state before work. */
const CONDITION_PURPOSE = 'Before';
/** The purposes a measurer chooses for a photo (hig_photopurpose; the feature photo is ★'s, not chosen here). */
export const CHOOSABLE_PURPOSES = ['Before', 'During', 'After', 'Asset', 'Defect', 'Measure'] as const;
const ACCURACY_TENTHS = 10;

/** The job's photo rows that are showing: one being removed is hidden at once. */
export const photoRowsOf = (records: readonly DeviceRecord[]): DeviceRecord[] => records.filter((r) => r.table === PHOTO_TABLE && !r.removing);

export const photoRowsOfArea = (records: readonly DeviceRecord[], areaId: string): DeviceRecord[] =>
  photoRowsOf(records).filter((r) => r.fields.hig_measureareaid === areaId);

/** Photos being removed on this device, not yet in CRM: hidden like removed ones. */
export const removingIds = (records: readonly DeviceRecord[]): string[] => records.filter((r) => r.table === PHOTO_TABLE && r.removing).map((r) => r.id);

const directionOfRow = (row: DeviceRecord): Direction | null => (typeof row.fields.hig_bearing === 'number' ? bearingToDirection(row.fields.hig_bearing) : null);

const text = (value: Fields[string]): string | null => (typeof value === 'string' && value ? value : null);

/** Where the position came from: the device's GPS for a photo taken here, the file's own data for one chosen from the library. */
function locationSource(place: PhotoPlace): string {
  if (!place.position) return 'None';
  return place.method === 'Camera' ? 'Device GPS' : "Photo's own data";
}

export interface NewPhotoRow {
  fileName: string;
  areaId: string;
  surface: { id: string; name: string } | null;
  feature: boolean;
  place: PhotoPlace;
  size: { width: number; height: number };
  /** The signed-in account's Entra object id when it was taken; null = not known on this device. */
  takenBy: string | null;
  /** On a job with a condition report: its visit and the area's visit area. */
  condition: { visitId: string; visitAreaId: string } | null;
}

/** What every photo row starts with: its name, area, purpose, where and when, and its condition links. */
function baseFields(photo: Pick<NewPhotoRow, 'fileName' | 'areaId' | 'feature' | 'condition'> & { surfaceId: string | null }, place: Omit<PhotoPlace, 'method' | 'camera'>): Fields {
  return {
    hig_name: photo.fileName,
    hig_measureareaid: photo.areaId,
    hig_surfaceid: photo.surfaceId,
    hig_purpose: photo.feature ? FEATURE_PHOTO : photo.condition ? CONDITION_PURPOSE : null,
    hig_takenon: place.takenAt,
    hig_bearing: place.bearing,
    hig_latitude: place.position?.latitude ?? null,
    hig_longitude: place.position?.longitude ?? null,
    hig_gpsaccuracy: place.position ? Math.round(place.position.accuracy * ACCURACY_TENTHS) / ACCURACY_TENTHS : null,
    hig_sitevisitid: photo.condition?.visitId ?? null,
    hig_visitareaid: photo.condition?.visitAreaId ?? null,
    hig_pinplacedbyhand: false,
  };
}

/** A new photo's row fields. Pure. */
export function newPhotoFields(photo: NewPhotoRow): Fields {
  const { place } = photo;
  return {
    ...baseFields({ ...photo, surfaceId: photo.surface?.id ?? null }, place),
    hig_surfacename: photo.surface?.name ?? null,
    hig_takenbyid: photo.takenBy,
    hig_locationsource: locationSource(place),
    hig_capturemethod: place.method,
    hig_camera: place.camera,
    hig_width: photo.size.width,
    hig_height: photo.size.height,
  };
}

/**
 * A row for a photo that has none — uploaded before the rows, or taken on the
 * older build — made when a condition report needs it as evidence. Only what
 * the older list or the device kept is known. Pure.
 */
export function earlierPhotoFields(
  photo: { fileName: string; areaId: string; surfaceId: string | null; feature: boolean },
  place: Omit<PhotoPlace, 'method' | 'camera'>,
  condition: NewPhotoRow['condition'],
): Fields {
  return { ...baseFields({ ...photo, condition }, place), hig_locationsource: place.position ? 'Device GPS' : 'None' };
}

/** What a photo row says about the photo, as an area screen reads it. Pure. */
export function rowFacts(row: DeviceRecord) {
  return {
    fileName: text(row.fields.hig_name) ?? '',
    surfaceId: text(row.fields.hig_surfaceid),
    direction: directionOfRow(row),
    uploaded: !!text(row.fields.hig_uploadedon),
    featurePhoto: row.fields.hig_purpose === FEATURE_PHOTO,
    missing: !!text(row.fields.hig_filemissingsince),
    takenOn: text(row.fields.hig_takenon),
    webUrl: text(row.fields.hig_weburl),
  };
}
